/* eslint-disable camelcase, @typescript-eslint/naming-convention */
import React from 'react';
import { AIGeneralBlockCard } from '../../AIGeneralBlockCard';
import { Text } from '../../../atoms/Text/Text';
import { Accordion } from '../../../molecules/Accordion/Accordion';
import { useAIAgentMessage } from '../../AIAgentMessage/context';
import { ExcludedItemsNotice, excludedItemsCopyLines } from '../ExcludedItemsNotice';
import './ExecutionPlanRendererStyles.scss';
const baseClass = 'qmb-ui-execution-plan-renderer';
function severityIcon(severity) {
    switch (severity) {
        case 'destructive':
            return 'fa-light fa-triangle-exclamation';
        case 'warning':
            return 'fa-light fa-circle-exclamation';
        default:
            return 'fa-light fa-circle-check';
    }
}
function StepRow({ step }) {
    return (React.createElement("div", { key: step.step_index, className: `${baseClass}__step` },
        React.createElement("span", { className: `${baseClass}__step-index` },
            step.step_index + 1,
            "."),
        React.createElement("i", { className: severityIcon(step.severity) }),
        React.createElement(Text, { variant: "body" }, step.summary)));
}
export function ExecutionPlanRenderer({ block }) {
    var _a, _b;
    const plan = block;
    const { onConfirm, onCancel, isActionTaken } = useAIAgentMessage();
    // Durable resolution (parity with ConfirmationRenderer): a plan that comes
    // back from history already approved/rejected renders terminal/disabled so it
    // can't be re-approved after a refresh. `isActionTaken` covers the live click.
    const isResolved = plan.status === 'approved' || plan.status === 'rejected';
    const actionsDisabled = isActionTaken || isResolved;
    const hasGroups = ((_b = (_a = plan.groups) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0) > 0;
    let stepListContent;
    let copyText;
    if (hasGroups) {
        const groups = plan.groups;
        const groupedIndices = new Set(groups.flatMap((g) => g.step_indices));
        const ungroupedSteps = plan.steps.filter((s) => !groupedIndices.has(s.step_index));
        const groupCopyLines = groups.map((g) => {
            const stepLines = g.step_indices
                .map((idx) => plan.steps.find((s) => s.step_index === idx))
                .filter(Boolean)
                .map((s) => `  ${s.step_index + 1}. ${s.summary}`);
            return [g.label, ...stepLines].join('\n');
        });
        const ungroupedCopyLines = ungroupedSteps.map((s) => `${s.step_index + 1}. ${s.summary}`);
        copyText = [plan.plan_summary, ...ungroupedCopyLines, ...groupCopyLines].join('\n');
        stepListContent = (React.createElement("div", { className: `${baseClass}__steps` },
            ungroupedSteps.map((step) => (React.createElement(StepRow, { key: step.step_index, step: step }))),
            groups.map((group, groupIndex) => (React.createElement("div", { key: `group-${groupIndex}`, className: `${baseClass}__group` },
                React.createElement(Accordion, { variant: "section", defaultExpanded: !group.collapse_default, title: React.createElement(Text, { variant: "body" }, group.label) }, group.step_indices
                    .map((idx) => plan.steps.find((s) => s.step_index === idx))
                    .filter(Boolean)
                    .map((step) => (React.createElement(StepRow, { key: step.step_index, step: step }))))))),
            React.createElement(Text, { variant: "body", className: `${baseClass}__affected` },
                plan.total_affected_records,
                " record",
                plan.total_affected_records !== 1 ? 's' : '',
                " affected")));
    }
    else {
        copyText = [plan.plan_summary, ...plan.steps.map((s) => `${s.step_index + 1}. ${s.summary}`)].join('\n');
        stepListContent = (React.createElement("div", { className: `${baseClass}__steps` },
            plan.steps.map((step) => (React.createElement(StepRow, { key: step.step_index, step: step }))),
            React.createElement(Text, { variant: "body", className: `${baseClass}__affected` },
                plan.total_affected_records,
                " record",
                plan.total_affected_records !== 1 ? 's' : '',
                " affected")));
    }
    copyText = [copyText, ...excludedItemsCopyLines(plan.excluded_items)].join('\n');
    // Rows refused before the plan was built (IP-16494): named up front so the
    // user knows what approving will not do.
    const excludedSection = plan.excluded_items && plan.excluded_items.length > 0
        ? [{ label: undefined, content: React.createElement(ExcludedItemsNotice, { items: plan.excluded_items }) }]
        : [];
    return (React.createElement(AIGeneralBlockCard, { variant: "default", sections: [
            {
                label: 'Execution Plan',
                content: React.createElement(Text, { variant: "body" }, plan.plan_summary),
                copyText
            },
            ...excludedSection,
            {
                label: undefined,
                content: stepListContent
            },
            ...(isResolved
                ? [
                    {
                        label: undefined,
                        content: (React.createElement(Text, { variant: "body", className: "qmb-ui-execution-plan-renderer__resolved" }, plan.status === 'approved' ? '✓ Approved' : '✕ Canceled'))
                    }
                ]
                : [])
        ], footerGroups: [
            {
                actions: [
                    {
                        id: 'confirm',
                        label: plan.confirm_label,
                        variant: 'primary',
                        onClick: () => onConfirm === null || onConfirm === void 0 ? void 0 : onConfirm(plan.plan_id),
                        disabled: actionsDisabled
                    },
                    {
                        id: 'cancel',
                        label: plan.cancel_label,
                        variant: 'default',
                        onClick: () => onCancel === null || onCancel === void 0 ? void 0 : onCancel(plan.plan_id),
                        disabled: actionsDisabled
                    }
                ]
            }
        ] }));
}
